import { Field as BaseField } from 'components/Wizards/Field/Field'
import { Button } from 'blaze-ui/Button'
import { useModals } from 'almanac-ui/Modal'
import { VisualElement, VisualElementsClient } from 'api/clients/VisualElementClient'
import { useField } from 'formik'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { useEffect, useMemo, useState } from 'react'
import { Selection } from '@react-types/shared'
import { ReactNode } from 'react'
import { Radio, RadioGroup } from 'components/RadioGroup'
import Styles from './TemplateAndCaption.module.scss'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import * as CSS from 'csstype'
import cx from 'classnames'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { compact, uniqBy } from 'lodash'
import { TemplatesModalV2 } from 'components/Templates/TemplatesModalV2/TemplatesModalV2'
import { useTemplatePreviewDimensions } from './useTemplatePreviewDimensions'
import { GeneratedDesignPreview } from './GeneratedDesignPreview/GeneratedDesignPreview'
import { useBasicVisualElementsAndVisualElementsByDescription } from 'hooks/useBasicVisualElementsAndVisualElementsByDescription'
import { BrandKitField } from 'components/Wizards/Wizard/Form/BrandKitField/BrandKitField'
import { isVideo } from 'components/ImagePickerModal/utils'
import { GeneratedArchetypePreview } from './GeneratedArchetypePreview/GeneratedArchetypePreview'
import { Archetype } from 'types'
import { ourUuid } from 'utils'
import { useNativeLayouts } from 'components/Wizards/hooks/useNativeLayouts'
import { Chrome } from './Chrome/Chrome'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { providerFromDesignChannelType } from 'components/PostPreviewModal/Types'

export type NativeArchetypeItem = { id: string; isNativeLayout: true; archetype: Archetype }

interface Props {
  onSelect: (template?: VisualElement) => void
  selectedTemplateFromModal?: VisualElement
  setSelectedTemplateFromModal: (template?: VisualElement) => void
  topic?: string
  isDisabled?: boolean
  label?: string
  description?: string
  showBrandKitInput?: boolean
  onBrandKitChange?: (brandKitId?: number) => void
  rightAction?: ReactNode
  initialValues?: object
  forceFetchVideoTemplates?: boolean
}

export const TemplateField = observer(
  ({
    onSelect,
    selectedTemplateFromModal,
    setSelectedTemplateFromModal,
    topic,
    isDisabled = false,
    label = 'Choose template for Blaze to customize',
    description = "We'll add your brand colors, custom copy and images after!",
    showBrandKitInput,
    onBrandKitChange,
    initialValues,
    forceFetchVideoTemplates,
  }: Props) => {
    const { openModal, closeStack } = useModals()
    const { visualElements } = useRootStore()
    const wizardSchema = useWizardSchema() as MultiStepSchema
    const [{ value: width }] = useField('width')
    const [{ value: height }] = useField('height')
    const [{ value: selectedTemplateId }] = useField('source_visual_element_id')
    const [{ value: initialMediaFile }] = useField('initial_media_file')
    const [, , { setValue: setArchetype }] = useField('archetype')
    const [{ value: caption }] = useField('caption')
    const [{ value: brand_kit_id }] = useField('brand_kit_id')
    const nativeLayouts = useNativeLayouts(brand_kit_id)
    const { enabled: nativeLayoutsEnabled, archetypes } = nativeLayouts[wizardSchema.meta.channel as DesignChannel]
    const brandKitQuery = useBrandKit(brand_kit_id)
    const externalIntegration = brandKitQuery.data?.brand_kit?.external_integrations.find(
      (i) => i.provider === providerFromDesignChannelType[wizardSchema.meta.channel as DesignChannel],
    )

    const initialTemplate = useMemo(
      () => (selectedTemplateId ? visualElements.getElement(selectedTemplateId) : undefined),
      [], // eslint-disable-line react-hooks/exhaustive-deps
    )
    const isInitialMediaVideo = isVideo({ url: initialMediaFile?.source_url, type: initialMediaFile?.mime_type })

    const { rows, columns } = useTemplatePreviewDimensions({
      width,
      height,
    })

    const items = rows * columns

    const [nativeArchetypes] = useState<NativeArchetypeItem[]>(() =>
      Array.from({ length: nativeLayoutsEnabled ? items : 0 }, (_, i) => ({
        id: ourUuid(),
        isNativeLayout: true,
        archetype: archetypes[i % archetypes.length],
      })),
    )
    const [selectedArchetypeId, setSelectedArchetypeId] = useState<string | undefined>(() => {
      if (nativeLayoutsEnabled) {
        return nativeArchetypes[0].id
      }
    })

    const style: CSS.Properties = {
      ['--preview-columns' as any]: `${columns}`,
      ['--preview-rows' as any]: `${rows}`,
    }

    const templatesQuery = useBasicVisualElementsAndVisualElementsByDescription(
      {
        width,
        height,
        description: topic || '',
        basicLayoutItems: items,
        has_replaceable: true,
        replaceable_has_background: true,
        byDescriptionItems: items,
        type: isInitialMediaVideo || forceFetchVideoTemplates ? ['video'] : undefined,
        additional_seed: (Math.random() + 1).toString(36).substring(2),
      },
      {
        enabled: !!topic && !nativeLayoutsEnabled,
        cacheTime: 2 * 1000 * 60,
        staleTime: Infinity,
      },
    )

    const templates = templatesQuery.data?.visual_elements || []
    const selectedTemplate = visualElements.getElement(selectedTemplateId)
    const templatesToDisplay = uniqBy(
      compact(
        ([initialTemplate, selectedTemplateFromModal] as Array<VisualElement | NativeArchetypeItem>).concat(
          nativeLayoutsEnabled ? nativeArchetypes : templates,
        ),
      ),
      'id',
    )

    useEffect(() => {
      if (!selectedTemplateId && !selectedArchetypeId) onSelect(selectedTemplate)
    }, [selectedTemplate, selectedArchetypeId]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleOpenModal = () => {
      openModal(
        TemplatesModalV2,
        {
          width,
          height,
          designChannel: wizardSchema.meta.channel as DesignChannel,
          types: isInitialMediaVideo ? new Set(['video']) : ('all' as Selection),
          onUse: async (templateId: number) => {
            const { data: template } = await VisualElementsClient.getElement(templateId)
            setSelectedTemplateFromModal(template)
            setSelectedArchetypeId(undefined)
            setArchetype(undefined)
            onSelect(template)
            closeStack()
          },
        },
        { newStack: true },
      )
    }

    return (
      <BaseField
        label={label}
        isRequired
        fieldGap={5}
        description={description}
        rightContent={
          <Button isDisabled={isDisabled} variant="ghost" size="sm" onPress={handleOpenModal}>
            See All Templates
          </Button>
        }
      >
        <RadioGroup aria-label="Title" value={selectedTemplateId}>
          {!nativeLayoutsEnabled && (!templatesQuery.isFetched || templatesQuery.isLoading) && (
            <div className={cx(Styles.grid)} style={style}>
              {Array.from({ length: items }).map((_, i) => (
                <ShimmeringColor aspectRatio={width / height} width="100%" key={i} />
              ))}
            </div>
          )}
          {(nativeLayoutsEnabled || (templatesQuery.isFetched && !templatesQuery.isLoading)) && (
            <div className={cx(Styles.grid)} style={style}>
              {templatesToDisplay.slice(0, items).map((item) => (
                <Radio
                  className={cx(Styles.radioOption, {
                    [Styles.selected]: selectedTemplateId === item.id || selectedArchetypeId === item.id,
                  })}
                  value={`${item.id}`}
                  hideRadio
                  data-testid="template-preview"
                  key={item.id}
                >
                  <div
                    onClick={() => {
                      if ('isNativeLayout' in item) {
                        onSelect(undefined)
                        setSelectedArchetypeId(`${item.id}`)
                        setArchetype(item.archetype)
                      } else {
                        onSelect(item as VisualElement)
                        setSelectedArchetypeId(undefined)
                        setArchetype(undefined)
                      }
                    }}
                    style={{
                      flex: 1,
                    }}
                  >
                    <Chrome
                      channel={wizardSchema.meta.channel as DesignChannel}
                      caption={caption}
                      integration={externalIntegration}
                    >
                      <div className={Styles.previewContainer} style={{ aspectRatio: width / height }}>
                        {'isNativeLayout' in item ? (
                          <GeneratedArchetypePreview
                            isSelected={selectedArchetypeId === item.id}
                            archetype={item.archetype}
                          />
                        ) : (
                          <GeneratedDesignPreview
                            isSelected={selectedTemplateId === item.id}
                            templateId={item.id as number}
                          />
                        )}
                      </div>
                    </Chrome>
                  </div>
                </Radio>
              ))}
            </div>
          )}
        </RadioGroup>
        {showBrandKitInput && (
          <div className={Styles.footerButtons}>
            {showBrandKitInput && <BrandKitField size="sm" onChange={onBrandKitChange} isDisabled={isDisabled} />}
          </div>
        )}
      </BaseField>
    )
  },
)
